import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../lib/api";
import { Search, X, Stethoscope } from "lucide-react";

/**
 * Typeahead provider picker backed by /api/provider-directory.
 * Contract:
 *   value      - current display name (string). Free text is allowed so callers keep the field
 *                editable even when the exact provider is not in the directory.
 *   onChange   - fires (name) when the user types or clears; use this to persist a display name.
 *   onPick     - optional. fires (directoryRow) when a directory entry is picked so callers can
 *                autofill role, organization, phone, email, fax, address, and NPI.
 *   role       - optional. narrows the directory search on the server.
 *   placeholder, testId, disabled - standard field passthroughs.
 * The picker debounces server search (200ms) and renders the top 20 matches, which keeps a
 * clinic of 500 providers responsive without pagination controls.
 */
export function ProviderSelect({ value, onChange, onPick, role, placeholder = "Search by name, org, specialty, or NPI", testId, disabled }) {
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = (value || "").trim();
    setLoading(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      const params = {};
      if (q) params.q = q;
      if (role) params.role = role;
      api.get("/provider-directory", { params })
        .then((r) => { setResults((r.data || []).slice(0, 20)); setLoading(false); })
        .catch(() => { setResults([]); setLoading(false); });
    }, 200);
    return () => timerRef.current && clearTimeout(timerRef.current);
  }, [value, open, role]);

  const filtered = useMemo(() => results, [results]);

  const pick = (row) => {
    onChange(row.name);
    if (typeof onPick === "function") onPick(row);
    setOpen(false);
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <input type="text" data-testid={testId} value={value || ""}
          disabled={disabled}
          onChange={(e) => { onChange(e.target.value); if (!open) setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder} autoComplete="off"
          className={`w-full rounded-lg border border-[#E5DFD3] bg-white pl-9 pr-9 py-2 text-sm text-[#3E2723] outline-none focus:border-[#8D6E63] ${disabled ? "opacity-60 cursor-not-allowed" : ""}`} />
        <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#7B6157]" />
        {value && !disabled && (
          <button type="button" data-testid={`${testId || "provider-select"}-clear`} aria-label="Clear provider"
            onClick={() => { onChange(""); setOpen(true); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-[#7B6157] hover:bg-[#F5F2EB]"><X size={14} /></button>
        )}
      </div>
      {open && !disabled && (
        <div data-testid={`${testId || "provider-select"}-dropdown`}
          className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto rounded-xl border border-[#EFEBE0] bg-white shadow-lg">
          {loading ? (
            <div className="p-3 text-center text-xs text-[#7B6157]">Searching provider directory…</div>
          ) : filtered.length === 0 ? (
            <div className="p-3 text-center text-xs text-[#7B6157]">No providers match. Type freely to enter a custom name.</div>
          ) : filtered.map((row) => (
            <button key={row.id} type="button" data-testid={`${testId || "provider-select"}-option-${row.id}`}
              onClick={() => pick(row)}
              className="w-full text-left px-3 py-2 text-sm transition-colors hover:bg-[#FDFCF8]">
              <div className="flex items-start gap-2">
                <span className="mt-0.5 rounded-full bg-[#F5F2EB] p-1 text-[#7B6157]"><Stethoscope size={11} /></span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#3E2723] truncate">{row.name}</span>
                    {row.in_network && <span className="rounded-full bg-[#E8F5E9] px-1.5 py-0.5 text-[9px] font-bold text-[#2E7D32]">In-network</span>}
                  </div>
                  <div className="mt-0.5 text-[11px] text-[#7B6157]">
                    {row.role || "-"}{row.specialty ? ` - ${row.specialty}` : ""}
                  </div>
                  <div className="mt-0.5 text-[11px] text-[#84675B] truncate">
                    {row.organization || "-"}{row.phone ? ` - ${row.phone}` : ""}{row.npi ? ` - NPI ${row.npi}` : ""}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
